import { useEffect, useState } from 'react';
import { headlessGet } from '../../lib/api';

interface FieldDefinition {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[];
}

interface BlockTypeDef {
  id: string;
  name: string;
  slug: string;
  fieldsJson: FieldDefinition[];
}

interface CustomBlockProps {
  props: {
    blockTypeSlug?: string;
    data?: Record<string, unknown>;
  };
}

export function CustomBlock({ props }: CustomBlockProps) {
  const { blockTypeSlug, data = {} } = props;
  const [def, setDef] = useState<BlockTypeDef | null>(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!blockTypeSlug) return;
    setLoading(true);
    setNotFound(false);

    headlessGet<BlockTypeDef>(`/block-definitions/${encodeURIComponent(blockTypeSlug)}`)
      .then((json) => setDef(json))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [blockTypeSlug]);

  if (!blockTypeSlug) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="w-5 h-5 animate-spin text-zinc-300" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
        </svg>
      </div>
    );
  }

  if (notFound || !def) return null;

  return (
    <div className="space-y-4">
      {def.fieldsJson.map((field) => {
        const value = data[field.key];
        if (value === undefined || value === null || value === '') return null;

        switch (field.type) {
          case 'boolean':
            if (!value) return null;
            return (
              <div key={field.key} className="flex items-center gap-2 text-sm text-zinc-700">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span>{field.label}</span>
              </div>
            );

          case 'rich_text':
            return (
              <div
                key={field.key}
                className="prose prose-sm max-w-none text-zinc-700"
                dangerouslySetInnerHTML={{ __html: String(value) }}
              />
            );

          case 'image':
            return (
              <div key={field.key}>
                <img
                  src={String(value)}
                  alt={field.label}
                  className="w-full rounded-lg object-cover"
                  loading="lazy"
                />
              </div>
            );

          case 'color':
            return (
              <div key={field.key} className="flex items-center gap-2">
                <div
                  className="w-5 h-5 rounded-full border border-zinc-200 flex-shrink-0"
                  style={{ backgroundColor: String(value) }}
                />
                <span className="text-sm text-zinc-500 font-mono">{String(value)}</span>
              </div>
            );

          case 'number':
            return (
              <div key={field.key} className="text-sm text-zinc-700">
                <span className="font-medium text-zinc-500">{field.label}: </span>
                {String(value)}
              </div>
            );

          case 'date':
          case 'datetime': {
            const d = new Date(String(value));
            const formatted = isNaN(d.getTime())
              ? String(value)
              : field.type === 'date'
                ? d.toLocaleDateString()
                : d.toLocaleString();
            return (
              <div key={field.key} className="text-sm text-zinc-700">
                <span className="font-medium text-zinc-500">{field.label}: </span>
                {formatted}
              </div>
            );
          }

          case 'array': {
            const items = Array.isArray(value) ? value : [];
            if (items.length === 0) return null;
            return (
              <div key={field.key} className="space-y-1">
                <p className="text-xs font-medium text-zinc-500">{field.label}</p>
                <ul className="list-disc list-inside space-y-0.5 text-sm text-zinc-700">
                  {items.map((item, i) => (
                    <li key={i}>{String(item)}</li>
                  ))}
                </ul>
              </div>
            );
          }

          default:
            return (
              <div key={field.key} className="text-sm text-zinc-700">
                {String(value)}
              </div>
            );
        }
      })}
    </div>
  );
}
